import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useCostAccess } from '~/hooks/useCanSeeCost';
import { cn } from '~/utils/cn';

import type { CostWaiting } from '../utils/cost-waiting';

import { RelativeTime } from './relative-time';

/**
 * "Paused 2 hours ago · View cost estimate" — the detail line under every "Awaiting cost
 * approval" state, linking to the estimate details page (where approvers decide). Customers
 * get "Paused 2 hours ago" only: the details page is lily.ai-only.
 */
export function CostApprovalWaitingNote({
  waiting,
  className,
  testId = 'cost-approval-waiting',
}: {
  waiting: CostWaiting;
  className?: string;
  /** `data-testid` root; derives `-paused-since` and `-view-estimate-link`. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  // Access, not amount visibility: with cost display switched off the estimate page still opens
  // (without $), so approvers keep the link.
  const { canSeeCost } = useCostAccess();
  if (!canSeeCost && !waiting.waitingSince) return null;
  return (
    <span
      data-testid={testId}
      className={cn('inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5', className)}
    >
      {waiting.waitingSince ? (
        <>
          <span>
            {t('costGate.paused')}{' '}
            <RelativeTime iso={waiting.waitingSince} testId={`${testId}-paused-since`} />
          </span>
          {canSeeCost ? <span aria-hidden="true">·</span> : null}
        </>
      ) : null}
      {canSeeCost ? (
        <Link
          data-testid={`${testId}-view-estimate-link`}
          to="/cost-approvals/$workflowId"
          params={{ workflowId: waiting.workflowId }}
          className="text-primary cursor-pointer font-medium hover:underline"
        >
          {t('costGate.viewEstimate')}
        </Link>
      ) : null}
    </span>
  );
}
